/**
 * Hook for managing filter, search, view mode, and pagination state.
 * Computes filtered accounts and grouped pipelines from inventory data.
 */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { Account, LambdaFunction, Pipeline } from '../types';

export type FilterType = 'all' | 'eol' | 'in-progress' | 'pipelines' | 'accounts' | 'job-executions' | 'brazil-eol' | null;

const ACCOUNTS_PER_PAGE = 10;
const PIPELINES_PER_PAGE = 20;

export interface GroupedPipeline {
  pipelineName: string;
  accounts: Array<{ pipeline: Pipeline; accountName: string; accountId: string }>;
}

export const useResourceFiltering = (
  accounts: Account[],
  precomputedData: {
    pipelinesGroupedByName: Map<string, Array<{ pipeline: Pipeline; accountName: string; accountId: string }>>;
    eolLambdaIds: Set<string>;
  }
) => {
  const [activeFilter, setActiveFilter] = useState<FilterType>(null);
  const [isViewTransitioning, setIsViewTransitioning] = useState(false);
  const [viewMode, setViewMode] = useState<'pipeline' | 'account'>('pipeline');
  const [searchQuery, setSearchQuery] = useState('');
  const [eolFilterType, setEolFilterType] = useState<'reached' | 'approaching'>('reached');

  // Pagination
  const [currentAccountPage, setCurrentAccountPage] = useState(1);
  const [currentPipelinePage, setCurrentPipelinePage] = useState(1);
  const [expandedPipelines, setExpandedPipelines] = useState<Set<string>>(new Set());

  // Reset pagination when filter changes
  useEffect(() => {
    setCurrentAccountPage(1);
    setCurrentPipelinePage(1);
  }, [activeFilter]);

  // Stable callbacks for debounced filter components
  const handleSearchFilterChange = useCallback((text: string) => {
    setSearchQuery(text);
  }, []);

  const togglePipelineExpanded = useCallback((pipelineId: string) => {
    setExpandedPipelines((prev) => {
      const next = new Set(prev);
      if (next.has(pipelineId)) {
        next.delete(pipelineId);
      } else {
        next.add(pipelineId);
      }
      return next;
    });
  }, []);

  /** Helper to transition view with brief loading state. */
  const transitionView = useCallback((callback: () => void) => {
    setIsViewTransitioning(true);
    callback();
    setTimeout(() => setIsViewTransitioning(false), 100);
  }, []);

  // Grouped pipelines for pipeline view (filtered, sorted, search-applied)
  const groupedPipelines: GroupedPipeline[] = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const isManualPipeline = (name: string) => name === 'Manual Deployed Stacks' || name === 'Manual Stack deployment' || name === 'Manually created';

    return Array.from(precomputedData.pipelinesGroupedByName.entries())
      .filter(([name]) => !isManualPipeline(name))
      .map(([name, instances]) => ({
        pipelineName: name,
        accounts: instances.filter((inst) => !inst.pipeline.isManual)
      }))
      .filter((group) => group.accounts.length > 0)
      .sort((a, b) => a.pipelineName.localeCompare(b.pipelineName))
      .filter((group) => {
        if (!query) return true;
        if (group.pipelineName.toLowerCase().includes(query)) return true;
        if (group.accounts.some((acc) => acc.accountName.toLowerCase().includes(query))) return true;
        if (
          group.accounts.some((acc) => acc.pipeline.lambdas.some((l) => l.name.toLowerCase().includes(query) || l.arn.toLowerCase().includes(query)))
        )
          return true;
        return false;
      });
  }, [precomputedData.pipelinesGroupedByName, searchQuery]);

  // Filtered accounts based on active filter and search
  const filteredAccounts = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const eolIds = precomputedData.eolLambdaIds;

    const matchesSearch = (lambda: LambdaFunction, pipeline: Pipeline, account: Account): boolean => {
      if (!query) return true;
      return (
        account.name.toLowerCase().includes(query) ||
        pipeline.name.toLowerCase().includes(query) ||
        lambda.name.toLowerCase().includes(query) ||
        lambda.arn.toLowerCase().includes(query)
      );
    };

    const filterLambda = (lambda: LambdaFunction, pipeline: Pipeline, account: Account): boolean => {
      if (activeFilter === 'eol' && !eolIds.has(lambda.id)) return false;
      if (activeFilter === 'in-progress' && lambda.status !== 'Running') return false;
      if (!matchesSearch(lambda, pipeline, account)) return false;
      return true;
    };

    // Fast path: no filtering needed
    if ((!activeFilter || activeFilter === 'all' || activeFilter === 'pipelines') && !query) {
      return accounts;
    }

    return accounts
      .map((account) => ({
        ...account,
        pipelines: account.pipelines
          .map((pipeline) => ({
            ...pipeline,
            lambdas: pipeline.lambdas.filter((l) => filterLambda(l, pipeline, account))
          }))
          .filter((pipeline) => pipeline.lambdas.length > 0)
      }))
      .filter((account) => account.pipelines.length > 0);
  }, [activeFilter, accounts, precomputedData.eolLambdaIds, searchQuery]);

  return {
    // Filter state
    activeFilter,
    setActiveFilter,
    isViewTransitioning,
    setIsViewTransitioning,
    viewMode,
    setViewMode,
    searchQuery,
    eolFilterType,
    setEolFilterType,
    handleSearchFilterChange,
    transitionView,
    // Pagination
    currentAccountPage,
    setCurrentAccountPage,
    currentPipelinePage,
    setCurrentPipelinePage,
    expandedPipelines,
    togglePipelineExpanded,
    ACCOUNTS_PER_PAGE,
    PIPELINES_PER_PAGE,
    // Computed
    groupedPipelines,
    filteredAccounts
  };
};
